import { useRef, useState } from 'react';

import type { ApolloError } from '@apollo/client';
import { useLazyQuery, useMutation } from '@apollo/client';
import type { GraphQLError } from 'graphql';

import type { CoachMemory } from 'bundles/ai-coach-platform/components/patterns/chat/settings/types';
import type {
  AiCoachPlatform_DeleteAllMemoriesForCurrentUserMutation as DeleteAllMemoriesForCurrentUserMutation,
  AiCoachPlatform_DeleteAllMemoriesForCurrentUserMutationVariables as DeleteAllMemoriesForCurrentUserMutationVariables,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachDeleteAllMemories';
import type {
  AiCoachPlatform_DeleteMemoriesForCurrentUserMutation as DeleteMemoriesForCurrentUserMutation,
  AiCoachPlatform_DeleteMemoriesForCurrentUserMutationVariables as DeleteMemoriesForCurrentUserMutationVariables,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachDeleteMemories';
import type {
  CoachMemoryFragment,
  AiCoachPlatform_SearchCoachMemoryQuery as SearchCoachMemoryQuery,
  AiCoachPlatform_SearchCoachMemoryQueryVariables as SearchCoachMemoryQueryVariables,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachSearchMemories';
import aiCoachDeleteAllMemoriesMutation from 'bundles/ai-coach-platform/queries/aiCoachDeleteAllMemories.graphql';
import aiCoachDeleteMemoriesMutation from 'bundles/ai-coach-platform/queries/aiCoachDeleteMemories.graphql';
import aiCoachSearchMemoriesQuery from 'bundles/ai-coach-platform/queries/aiCoachSearchMemories.graphql';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';
import localStorageEx from 'bundles/common/utils/localStorageEx';

// We don't need to track multi-deletion versus delete-all separately,
// since can only have one deletion task pending at any time,
// because UI is always blocked when deletion is in progress.
export const COACH_MEMORY_DELETED = 'AiCoachPlatform_MemoryDeleted';

const getMemoryDeletedPendingTask = (): string | null => {
  return localStorageEx.getItem<{ taskId: string } | null>(COACH_MEMORY_DELETED, JSON.parse, null)?.taskId ?? null;
};

const setMemoryDeletedPendingTask = (taskId: string) => {
  localStorageEx.setItem(COACH_MEMORY_DELETED, { taskId }, JSON.stringify);
};

const unsetMemoryDeletedPendingTask = () => {
  localStorageEx.removeItem(COACH_MEMORY_DELETED);
};

export const getPendingTask = (): string | null => {
  return getMemoryDeletedPendingTask();
};

export const setPendingTask = (taskId: string) => {
  setMemoryDeletedPendingTask(taskId);
};

export const clearPendingTask = () => {
  unsetMemoryDeletedPendingTask();
};

// number of memories to fetch on each query
const FETCH_LIMIT = 30;

const useCoachMemory = (): {
  loading: boolean;
  error?: ApolloError;
  userMemories: CoachMemory[];
  deleteMemories: (
    memoryIds: string[],
    isDeleteAll?: boolean
  ) => Promise<{ taskId: string | undefined; errors: readonly GraphQLError[] | undefined; onTaskComplete: () => void }>;
  fetchMemories: () => Promise<void>;
  fetchMoreMemories: () => Promise<{ error: ApolloError | undefined } | undefined>;
  total: number;
} => {
  const [userMemories, setUserMemories] = useState<CoachMemory[]>([]);
  const [totalElements, setTotalElements] = useState<number>(0);
  const cursorRef = useRef<string | null>(null);

  const [fetchQuery, { loading, refetch, error }] = useLazyQuery<
    SearchCoachMemoryQuery,
    SearchCoachMemoryQueryVariables
  >(aiCoachSearchMemoriesQuery, {
    variables: {
      limit: FETCH_LIMIT,
      cursor: null,
    },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'network-only',
  });

  const [deleteMemoryMutation] = useMutation<
    DeleteMemoriesForCurrentUserMutation,
    DeleteMemoriesForCurrentUserMutationVariables
  >(aiCoachDeleteMemoriesMutation, {
    context: { clientName: 'gatewayGql' },
  });

  const [deleteAllMemoryMutation] = useMutation<
    DeleteAllMemoriesForCurrentUserMutation,
    DeleteAllMemoriesForCurrentUserMutationVariables
  >(aiCoachDeleteAllMemoriesMutation, {
    context: { clientName: 'gatewayGql' },
  });

  const updateMemory = (data: SearchCoachMemoryQuery | undefined) => {
    if (data?.coachMemory?.searchMemory.pagination) {
      cursorRef.current = data?.coachMemory?.searchMemory.pagination.cursor ?? null;
      setTotalElements(data.coachMemory.searchMemory.pagination.totalElements ?? 0);
    }

    setUserMemories((prev) => [
      ...prev,
      ...(data?.coachMemory?.searchMemory.elements as CoachMemoryFragment[]).map((m) => ({
        id: m.memoryId,
        text: m.text,
      })),
    ]);
  };

  const fetchMemories = async () => {
    const { data, error: fetchError } = await fetchQuery({
      variables: { limit: FETCH_LIMIT },
    });

    if (fetchError) {
      captureMessageInSentry({ error: fetchError, source: 'useCoachMemory' });
      return Promise.reject();
    }

    updateMemory(data);

    return Promise.resolve();
  };

  const handleDeleteMemories = async (memoryIds: string[], isDeleteAll = false) => {
    if (isDeleteAll) {
      const { data, errors } = await deleteAllMemoryMutation();
      return {
        taskId: data?.AiCoach_DeleteAllMemoriesForCurrentUser.taskId,
        errors,
        // this callback allows us to execute this async once the task is actually complete
        onTaskComplete: () => {
          setUserMemories([]);
          setTotalElements(0);
        },
      };
    } else {
      const { data, errors } = await deleteMemoryMutation({
        variables: {
          input: {
            memoryIds,
          },
        },
      });

      return {
        taskId: data?.AiCoach_DeleteMemoriesForCurrentUser.taskId,
        errors,
        // this callback allows us to execute this async once the task is actually complete
        onTaskComplete: () => {
          setUserMemories(userMemories.filter((m) => !memoryIds.includes(m.id)));
          setTotalElements((prev) => prev - memoryIds.length);
        },
      };
    }
  };

  return {
    userMemories,
    loading,
    error,
    deleteMemories: handleDeleteMemories,
    fetchMemories,
    fetchMoreMemories: async () => {
      if (cursorRef.current) {
        const { data, error: fetchMoreError } = await refetch({ cursor: cursorRef.current, limit: FETCH_LIMIT });
        updateMemory(data);

        return { error: fetchMoreError };
      }
      return undefined;
    },
    total: totalElements,
  };
};

export default useCoachMemory;
